import { X, MessageCircle, Copy, DownloadIcon } from "lucide-react";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { GeneratedImageItem } from "@packages/api/madlenAPI.schemas";
import { Button, Text } from "@core/index";
import { motion } from "framer-motion";
import { useToast } from "@packages/contexts/ToastContext";

const ImagePreviewModal = ({
  images,
  setIsModalOpen,
  prompt,
}: {
  images: GeneratedImageItem[];
  setIsModalOpen: (isModalOpen: boolean) => void;
  prompt?: string;
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();
  const mainImage = images?.[0];

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  const handleDownload = async (imageUrl: string | undefined) => {
    if (!imageUrl) return;
    setIsLoading(true);

    try {
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = `generated-image-${Date.now()}.jpg`;
      link.click();
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      // Feedback for download action
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const copyPromptToClipboard = async () => {
    const currentPrompt = prompt || mainImage?.prompt;
    if (!currentPrompt) return;

    try {
      await navigator.clipboard.writeText(currentPrompt);
      addToast({
        title: t("imagen.promptCopied"),
        variant: "success",
        duration: 2000,
      });
    } catch (error) {
      console.error("Failed to copy:", error);
      addToast({
        title: t("imagen.promptCopyFailed"),
        variant: "error",
        duration: 2000,
      });
    }
  };

  // Focus trap
  useEffect(() => {
    const modalElement = modalRef.current;
    if (!modalElement) return;

    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    // Check for empty array
    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    firstFocusable?.focus();

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    modalElement.addEventListener("keydown", handleTabKey);
    return () => modalElement.removeEventListener("keydown", handleTabKey);
  }, [images]); // Dependency on images because focusable elements may change

  // If images is empty or undefined, show warning
  if (!images || images.length === 0) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md">
          <h2 className="text-xl font-bold mb-4 text-red-600 dark:text-red-400">
            {t("imagen.noImagesFound")}
          </h2>
          <p className="mb-6">{t("imagen.noImagesFoundDesc")}</p>
          <Button
            variant="solid"
            size="md"
            rounded="lg"
            bgColor="bg-gray-500 hover:bg-gray-600"
            textColor="text-white"
            onClick={handleClose}
          >
            {t("common.close")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/80"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      ref={modalRef}
    >
      <div className="relative flex flex-col bg-white dark:bg-gray-800 rounded-2xl w-full max-w-[95vw] h-[92vh]">
        <Button
          variant="outline"
          size="icon"
          rounded="full"
          className="absolute top-4 right-4 z-[9999] shadow-lg hover:shadow-xl"
          bgColor="bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700"
          textColor="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          borderColor="border-gray-200 dark:border-gray-600"
          leftIcon={<X size={28} />}
          onClick={handleClose}
          aria-label={t("common.close")}
        />
        <h2 id="modal-title" className="sr-only">
          {t("imagen.imagePreview")}
        </h2>
        {/* Main content area with fullscreen-fit image on the left and prompt on the right */}
        <div className="flex-1 min-h-0 p-3 sm:p-5">
          <div className="flex h-full gap-4">
            {/* Left: Image fills available space, fit to screen */}
            <div className="flex-1 h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700/50 shadow-lg overflow-hidden">
              {mainImage?.imageUrl && (
                <img
                  src={mainImage.imageUrl}
                  alt={t("imagen.viewFullImage")}
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>

            {/* Right: Prompt + Download button */}
            <div className="w-[320px] sm:w-[360px] lg:w-[420px] h-full flex flex-col gap-3">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-800/50 backdrop-blur-md rounded-xl p-4 border border-gray-200 dark:border-gray-700/50 shadow-lg overflow-auto"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-lime-600 dark:text-lime-400" />
                    <Text
                      variant="h3"
                      className="text-lg font-semibold text-gray-800 dark:text-gray-200"
                    >
                      {t("imagen.promptUsed")}
                    </Text>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    rounded="full"
                    className="flex items-center gap-1 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    leftIcon={<Copy className="w-4 h-4" />}
                    onClick={copyPromptToClipboard}
                  >
                    {t("common.copy")}
                  </Button>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                  <Text
                    variant="p"
                    className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap"
                  >
                    {prompt}
                  </Text>
                </div>
              </motion.div>

              <div className="pt-1">
                <Button
                  variant="solid"
                  size="md"
                  rounded="lg"
                  bgColor="bg-lime-500 hover:bg-lime-600"
                  textColor="text-white"
                  leftIcon={<DownloadIcon size={20} />}
                  disabled={isLoading}
                  className={`w-full ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    if (mainImage) {
                      handleDownload(mainImage.imageUrl);
                    }
                  }}
                  aria-busy={isLoading}
                >
                  {isLoading ? t("imagen.downloading") : t("common.download")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
